import React, { useEffect, useMemo, useRef, useState } from 'react'

import {
  TPostInviteCurrentUserToDashboardGroup,
  TPostInviteToDashboardGroupPermission,
  TPostRevokeAccess
} from 'redux/api/DashboardGroups/types'

import { EAccountRole } from 'types/GlobalAccount'
import { TDashboardGroup, WhoCanView } from 'types/GlobalDashboardGroup'

import { useAccounts } from 'redux/hooks/Accounts'
import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import { useGroups } from 'redux/hooks/Groups'
import { useAllowEditDashboardGroup } from 'redux/hooks/Permissions'
import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Divider from 'components_new/atoms/Divider'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import List from 'components_new/atoms/List'
import TooltipWrapper from 'components_new/atoms/TooltipWrapper'

import FormToggle from 'components_new/molecules/FormToggle'
import GenericListItem from 'components_new/molecules/GenericListItem'
import SelectSearch from 'components_new/molecules/SelectSearch'

import { formatGroupOption, formatUserOption } from 'utils/functions'
import { sortAlphabeticalAsc } from 'utils/sortHelper'

import { getAccountPermissions, getGroupPermissions } from './utils'
interface PermissionProps {
  dashboardGroup: TDashboardGroup
  onInvite: (newPermission: TPostInviteCurrentUserToDashboardGroup) => void
  // set if controlled by state. (publishing dashboard)
  permissions?: TPostInviteToDashboardGroupPermission[]
  revokeAccess: (body: TPostRevokeAccess) => void
  setWhoCanView: (value: WhoCanView) => void
  whoCanView?: WhoCanView
}

const DashboardPermissions = (props: PermissionProps) => {
  const {
    dashboardGroup,
    onInvite,
    revokeAccess,
    setWhoCanView,
    permissions = dashboardGroup.share_settings.permissions,
    whoCanView = dashboardGroup.share_settings.who_can_view
  } = props

  const { t, translateEnum } = useTranslation()
  const { accounts, accountBody } = useAccounts()
  const { allGroups } = useGroups()
  const { account: signedInAccount, loading: accountLoading } =
    useAccountSettings()
  const allowEditDashboardGroup = useAllowEditDashboardGroup(dashboardGroup.id)

  const userPermissions = useMemo(
    () =>
      getAccountPermissions(
        accounts,
        accountBody,
        permissions,
        signedInAccount,
        dashboardGroup.owner
      ).sort((a, b) => sortAlphabeticalAsc(a.name, b.name)),
    [permissions, accounts, signedInAccount, dashboardGroup.owner]
  )

  const groupPermissions = useMemo(
    () => getGroupPermissions(allGroups, permissions),
    [permissions, allGroups]
  )

  const notAddedGroupOptions = useMemo(
    () =>
      Object.values(allGroups)
        .filter(
          (group) =>
            !groupPermissions.some(
              (currentGroup) => currentGroup.id === group.id
            )
        )
        .map((group) => formatGroupOption(group, t)),
    [userPermissions, allGroups]
  )

  const notAddedUserOptions = useMemo(
    () =>
      Object.values(accounts)
        .filter(
          (account) =>
            account.role === EAccountRole.VIEW &&
            // remove self
            account.id !== signedInAccount?.id &&
            // remove owner of dashboard from user options
            account.id !== dashboardGroup.owner &&
            // remove users with access
            !userPermissions.some(
              (currentUser) => currentUser.accountId === account.id
            )
        )
        .map((account) => formatUserOption(account)),
    [userPermissions, accounts, signedInAccount, dashboardGroup.owner]
  )

  const ref = useRef<HTMLElement>(null)

  const [scrollTop, setScrollTop] = useState(true)
  const [scrollBottom, setScrollBottom] = useState(true)

  useEffect(() => {
    const element = ref.current
    const handleScroll = () => {
      // Check scroll at top
      if (element && element.scrollTop === 0) {
        setScrollTop(true)
      } else {
        setScrollTop(false)
      }
      // Check scroll at bottom
      if (
        element &&
        element.scrollTop > element.scrollHeight - element.offsetHeight
      ) {
        setScrollBottom(true)
      } else {
        setScrollBottom(false)
      }
    }

    if (element) {
      element.addEventListener('scroll', handleScroll)

      // Clean-up
      return () => {
        element.removeEventListener('scroll', handleScroll)
      }
    }

    return
  }, [])

  if (accountLoading) {
    return null
  }

  const isPrivate = whoCanView === WhoCanView.ONLY_ADDED_USERS

  const availableOptions = isPrivate
    ? [...notAddedGroupOptions, ...notAddedUserOptions]
    : notAddedGroupOptions

  return (
    <Box>
      <FormToggle
        body={t('dashboards', 'only_owners_and_added_can_use')}
        icon={{ name: 'Private' }}
        onChange={() =>
          setWhoCanView(
            isPrivate ? WhoCanView.ORGANIZATION : WhoCanView.ONLY_ADDED_USERS
          )
        }
        title={t('dashboards', 'private_dashboard')}
        value={isPrivate}
      />

      <Divider sx={{ my: 2 }} />
      <SelectSearch
        closeOnSelect={true}
        color="primary"
        disableClearable={true}
        label={`${t('global', 'add')} ${
          isPrivate
            ? t('dashboards', 'user_or_group')
            : t('groups', 'group', true)
        }`}
        onChange={(option) => {
          const isGroup = option.avatar === 'group'
          const account = isGroup ? null : accountBody[option.id as string]

          onInvite({
            account_id: isGroup ? null : (option.id as string),
            group_id: isGroup ? (option.id as string) : null,
            company_group_id: account?.company_group_id
          })
        }}
        multiple={false}
        options={availableOptions}
        selected={null}
        size={'medium'}
        sort={true}
        sx={{ mb: 2 }}
      />

      <List
        ref={ref}
        dense={true}
        sx={{
          pt: 0,
          maxHeight: '30vh',
          overflowY: 'auto',
          borderTop: scrollTop ? undefined : '1px solid',
          borderBottom: scrollBottom ? undefined : '1px solid',
          borderColor: 'divider'
        }}
      >
        {isPrivate
          ? userPermissions.map((user) => (
            <GenericListItem
              actions={
                allowEditDashboardGroup ? (
                  <TooltipWrapper
                    title={
                      user.owner
                        ? t('dashboards', 'cannot_remove_owner')
                        : user.role === EAccountRole.ADMIN
                          ? t('dashboards', 'cannot_remove_admin')
                          : t('dashboards', 'remove_access')
                    }
                  >
                    <IconButton
                      disabled={
                        user.owner || user.role === EAccountRole.ADMIN
                      }
                      onClick={() =>
                        revokeAccess({ account_id: user.accountId })
                      }
                      size="small"
                    >
                      <Icon
                        fontSize="small"
                        name="RemoveCircleOutlineOutlined"
                      />
                    </IconButton>
                  </TooltipWrapper>
                ) : null
              }
              body={user.email}
              chip={{
                label: user.owner
                  ? t('global', 'owner')
                  : translateEnum('account_roles', user.role).title
              }}
              hasHover={true}
              key={user.accountId}
              title={user.name}
              variant="user"
            />
          ))
          : null}

        {/* @TODO: Implement placeholder: */}
        {groupPermissions.map((group) => (
          <GenericListItem
            key={group.id}
            actions={
              allowEditDashboardGroup ? (
                <TooltipWrapper title={t('dashboards', 'remove_access')}>
                  <IconButton
                    onClick={() => revokeAccess({ group_id: group.id })}
                    size="small"
                  >
                    <Icon fontSize="small" name="RemoveCircleOutlineOutlined" />
                  </IconButton>
                </TooltipWrapper>
              ) : undefined
            }
            body={`${group.members.length} ${t('global', 'members', true)}`}
            chip={{
              label: t('groups', 'group')
            }}
            color={group.profile_color}
            hasHover={true}
            title={group.name}
            variant="group"
          />
        ))}
      </List>
    </Box>
  )
}

export default DashboardPermissions
